import {Component, signal, computed, inject, effect, ChangeDetectionStrategy} from "@angular/core"
import {narrowInstance, IsNonNull} from "@cm/utils"
import {TitleCasePipe} from "@angular/common"
import {MatTooltip} from "@angular/material/tooltip"
import {MatMenuModule} from "@angular/material/menu"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {
    InputNode,
    ExportNode,
    OutputNode,
    isValueLikeNode,
    MathOperator,
    getRootTemplateNodeForObjectPath,
    getTemplateNodeForObjectPath,
    TemplateGraph,
    MoveMaterial,
} from "@cm/template-nodes"
import {
    Mattress,
    MattressComponentGraph,
    MattressReferenceFrame,
    MattressComponentInstance,
    MattressMesh,
    MattressCurve,
    MattressCut,
    MattressComputedCurve,
    MattressComponentGraphGenerator,
    MattressCurveInput,
    MattressReferenceFrameInput,
    MattressCurveOutput,
    MattressReferenceFrameOutput,
    MattressExport,
    isMattressFocusedComponentInstancePath,
    setMattressOnContextOtherData,
    MattressGroup,
    isMattressComponentGraphNode,
    standardModelTemplateSeeders,
    initRootComponentGraphForStackableMattressStandardModel,
    insertMaterialInputsForStoredMeshes,
    removeMaterialInputsForStoredMeshes,
    insertDefaultCamera,
    loadDefaultCamera,
    insertLighting,
    requireLightingTemplateLegacyId,
    resolveMattressAssetLegacyIds,
} from "@cm/mattress-nodes"
import type {ValueLikeNode} from "@cm/template-nodes"
import type {MattressComponentGraphNode, MattressFocusedComponentInstancePath, MattressSeedContext, StandardModelSeederFunctions} from "@cm/mattress-nodes"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {MattressCurveInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-curve-inspector/mattress-curve-inspector.component"
import {MattressCutInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-cut-inspector/mattress-cut-inspector.component"
import {MattressReferenceFrameInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-reference-frame-inspector/mattress-reference-frame-inspector.component"
import {MattressComponentGraphInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-component-graph-inspector/mattress-component-graph-inspector.component"
import {MattressComponentInstanceInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-component-instance-inspector/mattress-component-instance-inspector.component"
import {InputInspectorComponent} from "@template-editor/components/inspectors/input-inspector/input-inspector.component"
import {MatDialogModule} from "@angular/material/dialog"
import {MattressInspectorViewType} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-inspector-view-type-selector/mattress-inspector-view-type-selector.component"
import {MattressMeshInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-mesh-inspector/mattress-mesh-inspector.component"
import {MattressInputInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-input-inspector/mattress-input-inspector.component"
import {MattressComputedCurveInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-computed-curve-inspector/mattress-computed-curve-inspector.component"
import {MattressComponentGraphGeneratorInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-component-graph-generator-inspector/mattress-component-graph-generator-inspector.component"
import {MattressExportInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-export-inspector/mattress-export-inspector.component"
import {MattressOutputInspectorComponent} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-output-inspector/mattress-output-inspector.component"
import {ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"
import {ExportInspectorComponent} from "@template-editor/components/inspectors/export-inspector/export-inspector.component"
import {ValueInspectorComponent} from "@template-editor/components/inspectors/value-inspector/value-inspector.component"
import {MattressInspectorService} from "@template-editor/components/inspectors/mattress-inspector/mattress-inspector.service"
import {MathOperatorInspectorComponent} from "@template-editor/components/inspectors/math-operator-inspector/math-operator-inspector.component"
import {GenericInspectorComponent} from "@template-editor/components/inspectors/generic-inspector/generic-inspector.component"
import {ListItemComponent} from "@common/components/item/list-item/list-item.component"
import {TemplateMenuComponent} from "@template-editor/components/template-menu/template-menu.component"
import {TemplateMenuSectionComponent} from "@template-editor/components/template-menu-section/template-menu-section.component"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {SiloService} from "@common/services/auth/silo.service"
import {SystemRole} from "@generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {
    GetDefaultCameraTemplateForMattressInspectorGQL,
    GetMaterialLegacyIdsForMattressInspectorGQL,
    GetTemplateLegacyIdsForMattressInspectorGQL,
} from "@template-editor/components/inspectors/mattress-inspector/mattress-inspector.generated"

type SeederVariant = keyof StandardModelSeederFunctions

const nonSuperadminStandardModels = new Set<string>(["simple pocket", "simple waterfall", "waterfall BS006A", "waterfall BS007A", "waterfall BS012A"])

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-mattress-inspector",
    templateUrl: "./mattress-inspector.component.html",
    styleUrl: "./mattress-inspector.component.scss",
    providers: [MattressInspectorService],
    imports: [
        MatTooltip,
        MatMenuModule,
        InspectorSectionComponent,
        MatDialogModule,
        MattressCurveInspectorComponent,
        MattressCutInspectorComponent,
        MattressReferenceFrameInspectorComponent,
        MattressComponentGraphInspectorComponent,
        MattressComponentInstanceInspectorComponent,
        MattressMeshInspectorComponent,
        InputInspectorComponent,
        ExportInspectorComponent,
        MattressInputInspectorComponent,
        MattressComputedCurveInspectorComponent,
        MattressComponentGraphGeneratorInspectorComponent,
        MattressExportInspectorComponent,
        MattressOutputInspectorComponent,
        ValueSlotComponent,
        ValueInspectorComponent,
        MathOperatorInspectorComponent,
        GenericInspectorComponent,
        ListItemComponent,
        TemplateMenuComponent,
        TemplateMenuSectionComponent,
        TitleCasePipe,
    ],
    standalone: true,
})
export class MattressInspectorComponent extends BaseInspectorComponent<Mattress> {
    mattressInspectorService = inject(MattressInspectorService)
    private readonly notifications = inject(NotificationsService)
    private readonly silo = inject(SiloService)
    private readonly defaultCameraTemplateGql = inject(GetDefaultCameraTemplateForMattressInspectorGQL)
    private readonly templateLegacyIdsGql = inject(GetTemplateLegacyIdsForMattressInspectorGQL)
    private readonly materialLegacyIdsGql = inject(GetMaterialLegacyIdsForMattressInspectorGQL)

    readonly $viewType = signal<MattressInspectorViewType>("geometry")
    readonly $showComponentGraphView = signal(false)

    readonly $selectedNodes = computed(() =>
        this.sceneManagerService
            .$selectedObjectPathParts()
            .map<MattressComponentGraphNode | false>((part) => {
                if (getRootTemplateNodeForObjectPath(part.path) !== this.$node()) return false
                const node = getTemplateNodeForObjectPath(part.path)
                if (!node || !isMattressComponentGraphNode(node)) return false
                return node
            })
            .filter((node): node is MattressComponentGraphNode => Boolean(node)),
    )

    readonly $inspectedNode = signal<MattressComponentGraphNode | undefined>(undefined)

    readonly $focusedComponentInstancePath = signal<MattressFocusedComponentInstancePath>("root")
    readonly $focusedComponentInstance = computed<MattressComponentInstance | "root">(() => {
        const focusedInstancePath = this.$focusedComponentInstancePath()
        if (focusedInstancePath === "root") return "root"
        const focusedInstance = focusedInstancePath[focusedInstancePath.length - 1]
        if (!focusedInstance) throw new Error("Focused component instance is undefined!")
        return focusedInstance
    })
    readonly $focusedComponentInstanceGraph = computed<MattressComponentGraph>(() => {
        const focusedInstance = this.$focusedComponentInstance()
        if (focusedInstance === "root") return this.$node().parameters.root
        if (focusedInstance.parameters.graph === null) throw new Error("Focused component instance graph is null!")
        if (!(focusedInstance.parameters.graph instanceof MattressComponentGraph))
            throw new Error("Focused component instance graph is not a mattress component graph!")
        return focusedInstance.parameters.graph
    })
    onFocusedComponentInstancePathChanged($event: unknown) {
        if (!isMattressFocusedComponentInstancePath($event)) return
        this.$focusedComponentInstancePath.set($event)
    }

    focusedComponentInstancePathOneStepUp() {
        const currentPath = this.$focusedComponentInstancePath()
        const newPath = (() => {
            if (currentPath === "root") return "root"
            else if (currentPath.length === 1) return "root"
            return currentPath.slice(0, -1)
        })()
        this.$focusedComponentInstancePath.set(newPath)
    }

    readonly $inspectorHeaderText = computed(() => {
        const focusedInstance = this.$focusedComponentInstance()
        if (focusedInstance === "root") return "Root"
        const focusedInstanceGraph = this.$focusedComponentInstanceGraph()
        return `${focusedInstanceGraph.parameters.name} (${focusedInstance.parameters.name})`
    })

    readonly $inspectedCurve = computed(() => narrowInstance(this.$inspectedNode(), MattressCurve))
    readonly $inspectedCut = computed(() => narrowInstance(this.$inspectedNode(), MattressCut))
    readonly $inspectedReferenceFrame = computed(() => narrowInstance(this.$inspectedNode(), MattressReferenceFrame))
    readonly $inspectedComponentGraph = computed(() => narrowInstance(this.$inspectedNode(), MattressComponentGraph))
    readonly $inspectedComponentInstance = computed(() => narrowInstance(this.$inspectedNode(), MattressComponentInstance))
    readonly $inspectedMesh = computed(() => narrowInstance(this.$inspectedNode(), MattressMesh))
    readonly $inspectedComputedCurve = computed(() => narrowInstance(this.$inspectedNode(), MattressComputedCurve))
    readonly $inspectedComponentGraphGenerator = computed(() => narrowInstance(this.$inspectedNode(), MattressComponentGraphGenerator))
    readonly $inspectedTemplateInput = computed(() => narrowInstance(this.$inspectedNode(), InputNode))
    readonly $inspectedMattressInput = computed(() => {
        const node = this.$inspectedNode()
        return narrowInstance(node, MattressCurveInput) ?? narrowInstance(node, MattressReferenceFrameInput)
    })
    readonly $inspectedTemplateExport = computed(() => narrowInstance(this.$inspectedNode(), ExportNode))
    readonly $inspectedMattressExport = computed(() => narrowInstance(this.$inspectedNode(), MattressExport))
    readonly $inspectedTemplateOutput = computed(() => narrowInstance(this.$inspectedNode(), OutputNode))
    readonly $inspectedMattressOutput = computed(() => {
        const node = this.$inspectedNode()
        return narrowInstance(node, MattressCurveOutput) ?? narrowInstance(node, MattressReferenceFrameOutput)
    })
    readonly $inspectedValue = computed<ValueLikeNode | undefined>(() => {
        const node = this.$inspectedNode()
        return node && isValueLikeNode(node) ? node : undefined
    })
    readonly $inspectedMathOperator = computed(() => narrowInstance(this.$inspectedNode(), MathOperator))
    readonly $inspectedMoveMaterial = computed(() => narrowInstance(this.$inspectedNode(), MoveMaterial))
    readonly $inspectedGroup = computed(() => narrowInstance(this.$inspectedNode(), MattressGroup))

    constructor() {
        super()

        effect(() => {
            const mattress = this.$node()
            this.mattressInspectorService.setMattressNode(mattress)
        })

        effect(() => {
            const selectedNodes = this.$selectedNodes()
            if (selectedNodes.length === 1) {
                this.$inspectedNode.set(selectedNodes[0])
            } else if (selectedNodes.length === 0) {
                this.$inspectedNode.set(undefined)
            }
        })

        effect(() => {
            const focusedComponentInstanceGraph = this.$focusedComponentInstanceGraph()
            const mattress = this.$node()
            if (focusedComponentInstanceGraph !== mattress.parameters.root) this.$inspectedNode.set(focusedComponentInstanceGraph)
        })

        effect(() => {
            const focusedComponentInstancePath = this.$focusedComponentInstancePath()
            const mattress = this.$node()
            setMattressOnContextOtherData(
                mattress,
                focusedComponentInstancePath !== "root" ? {focusedComponentInstancePath} : undefined,
                this.sceneManagerService,
            )
        })

        this.destroyRef.onDestroy(() => {
            const mattress = this.$node()
            setMattressOnContextOtherData(mattress, undefined, this.sceneManagerService)
        })
    }

    override ngOnInit() {
        super.ngOnInit()

        const infoNode = this.$node().getMattressInfoNode(this.sceneManagerService)
        if (!infoNode) return
        const currentFocusedComponentInstancePath = this.$focusedComponentInstancePath()
        if (currentFocusedComponentInstancePath !== infoNode.focusedComponentInstancePath)
            this.$focusedComponentInstancePath.set(infoNode.focusedComponentInstancePath)
    }

    modifyTemplateGraph = (modifier: (x: TemplateGraph) => void) => this.sceneManagerService.modifyTemplateGraph(modifier)

    initAsStandardModel() {
        this.modifyTemplateGraph(() => {
            const rootNodes = initRootComponentGraphForStackableMattressStandardModel()
            this.$node().parameters.root.parameters.nodes.updateParameters({
                list: [
                    rootNodes.mattressMesh,
                    rootNodes.dimensionsGroup,
                    rootNodes.topPartGroup,
                    rootNodes.middlePartGroup,
                    rootNodes.bottomPartGroup,
                    rootNodes.handlesGroup,
                ],
            })
        })
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    insertMaterialInputsForMeshes() {
        this.modifyTemplateGraph((templateGraph) => {
            insertMaterialInputsForStoredMeshes(templateGraph)
        })
    }

    removeMaterialInputsForMeshes() {
        this.modifyTemplateGraph((templateGraph) => {
            removeMaterialInputsForStoredMeshes(templateGraph)
        })
    }

    async addDefaultCamera() {
        try {
            const camera = await loadDefaultCamera(
                async (id) => (await fetchThrowingErrors(this.defaultCameraTemplateGql)({id})).template.latestRevision?.graph,
            )
            insertDefaultCamera(this, camera)
        } catch (error) {
            this.notifications.showError(error, 0, "Close")
        }
    }

    async addDefaultLighting() {
        try {
            insertLighting(this, requireLightingTemplateLegacyId(await this.loadSeedContext()))
        } catch (error) {
            this.notifications.showError(error, 0, "Close")
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////
    private readonly $isSuperadminSilo = computed(() => this.silo.$silo()?.systemRole === SystemRole.Superadmin)

    readonly $standardModelSeederGroups = computed(() => {
        const isSuperadminSilo = this.$isSuperadminSilo()
        const variants: SeederVariant[] = isSuperadminSilo ? ["master", "instance"] : ["master"]
        return Object.entries(standardModelTemplateSeeders)
            .map(([groupKey, seeders]) => ({
                groupKey,
                seeders: Object.entries(seeders)
                    .filter(([key]) => isSuperadminSilo || nonSuperadminStandardModels.has(key))
                    .flatMap(([key, value]) => variants.map((variant) => ({key, value, variant}))),
            }))
            .filter((group) => group.seeders.length > 0)
    })

    private seedContext?: Promise<MattressSeedContext>

    private loadSeedContext(): Promise<MattressSeedContext> {
        this.seedContext ??= resolveMattressAssetLegacyIds({
            templateLegacyIds: async (ids) => (await fetchThrowingErrors(this.templateLegacyIdsGql)({ids})).templates.filter(IsNonNull),
            materialLegacyIds: async (ids) => (await fetchThrowingErrors(this.materialLegacyIdsGql)({ids})).materials.filter(IsNonNull),
        }).catch((error: unknown) => {
            this.seedContext = undefined
            throw error
        })
        return this.seedContext
    }

    async runSeeder(seeder: StandardModelSeederFunctions, variant: SeederVariant) {
        try {
            await seeder[variant](this, await this.loadSeedContext())
        } catch (error) {
            this.notifications.showError(error, 0, "Close")
        }
    }
}
